import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Layers, Calendar, Users, Mail, FileText, LogOut, ChevronLeft, ChevronRight, Settings, ChevronDown, ChevronUp, GitBranch, MessageSquarePlus, ClipboardList, Heart, Film, UserCircle, FileSignature, Package, ListTodo, Database, CheckSquare, AlertCircle, BarChart3, CreditCard, DollarSign, Target, BookOpen, Video, Play, X, VideoOff, Workflow, Newspaper, Crosshair } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SANDBOX_MODE = process.env.REACT_APP_SANDBOX_MODE === 'true';

// Space Background Component for Dark Mode
const SpaceBackground = () => {
  // Generate stars only once with useMemo
  const stars = useMemo(() => {
    return Array.from({ length: 80 }, (_, i) => ({
      id: i,
      top: (i * 17 + 23) % 100,
      left: (i * 31 + 7) % 100,
      delay: (i * 0.3) % 4,
      duration: 2 + (i % 3),
      size: 1 + (i % 2),
      opacity: 0.3 + ((i % 5) * 0.1)
    }));
  }, []);

  const shootingStars = useMemo(() => [
    { delay: 0, top: 12, left: 15 },
    { delay: 5, top: 28, left: 55 },
    { delay: 10, top: 8, left: 75 },
    { delay: 15, top: 35, left: 35 }
  ], []);

  return (
    <>
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.8; }
        }
        @keyframes shootingStar {
          0% { transform: translateX(0) translateY(0); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: translateX(200px) translateY(200px); opacity: 0; }
        }
        @keyframes flySpaceship {
          0% { transform: translateX(-50px) rotate(45deg); opacity: 0; }
          10% { opacity: 0.7; }
          90% { opacity: 0.7; }
          100% { transform: translateX(calc(100vw + 50px)) rotate(45deg); opacity: 0; }
        }
        .space-star {
          animation: twinkle 3s ease-in-out infinite;
        }
        .space-shooting-star {
          width: 3px;
          height: 3px;
          background: linear-gradient(45deg, white, transparent);
          border-radius: 50%;
          animation: shootingStar 4s ease-in-out infinite;
        }
        .space-shooting-star::after {
          content: '';
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 60px;
          height: 1px;
          background: linear-gradient(to left, rgba(255,255,255,0.8), transparent);
        }
        .space-ship {
          animation: flySpaceship 20s linear infinite;
          font-size: 16px;
        }
      `}</style>
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a12] via-[#0f0f1a] to-[#0a0a15]" />
        
        {/* Stars */}
        {stars.map(star => (
          <div
            key={star.id}
            className="absolute rounded-full bg-white space-star"
            style={{
              top: `${star.top}%`,
              left: `${star.left}%`,
              width: star.size,
              height: star.size,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              opacity: star.opacity
            }}
          />
        ))}
        
        {/* Shooting Stars */}
        {shootingStars.map((star, i) => (
          <div
            key={`shooting-${i}`}
            className="absolute space-shooting-star"
            style={{
              top: `${star.top}%`,
              left: `${star.left}%`,
              animationDelay: `${star.delay}s`
            }}
          />
        ))}
        
        {/* Spaceships */}
        <div className="absolute space-ship" style={{ top: '15%', animationDelay: '0s' }}>🚀</div>
        <div className="absolute space-ship" style={{ top: '45%', animationDelay: '8s' }}>🚀</div>
        <div className="absolute space-ship" style={{ top: '75%', animationDelay: '16s' }}>🚀</div>
      </div>
    </>
  );
};

// Compact Video Help Icon Component
const VideoHelpIcon = ({ section }) => {
  const { isDark } = useTheme();
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideoUrl = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/help-videos/${section}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setVideoUrl(data.video_url);
        }
      } catch (error) {
        console.error('Failed to fetch video URL:', error);
      } finally {
        setLoading(false);
      }
    };
    if (section) {
      fetchVideoUrl();
    }
  }, [section]);

  const getEmbedUrl = (url) => {
    if (!url) return null;
    let videoId = null;
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) videoId = watchMatch[1];
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) videoId = shortMatch[1];
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (embedMatch) videoId = embedMatch[1];
    if (videoId) return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    return url;
  };

  return (
    <>
      <button
        onClick={() => setShowVideo(true)}
        className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'} transition-colors`}
        title="Watch tutorial video"
      >
        <Play className="w-4 h-4 text-[#A2FF00]" fill="#A2FF00" />
      </button>

      {showVideo && (
        <div className="fixed top-16 right-4 z-50">
          <div className={`${isDark ? 'bg-[#111] border-white/20' : 'bg-white border-gray-300'} border rounded-lg shadow-2xl overflow-hidden`}>
            <div className={`flex items-center justify-between px-3 py-2 ${isDark ? 'bg-[#0A0A0A] border-b border-white/10' : 'bg-gray-100 border-b border-gray-200'}`}>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Tutorial Video
              </span>
              <button
                onClick={() => setShowVideo(false)}
                className={`p-1 rounded hover:bg-white/10 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="w-[400px] h-[225px] bg-black flex items-center justify-center">
              {loading ? (
                <div className="text-gray-400 text-sm">Loading...</div>
              ) : videoUrl ? (
                <iframe
                  width="400"
                  height="225"
                  src={getEmbedUrl(videoUrl)}
                  title="Tutorial Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <div className="text-center p-4">
                  <VideoOff className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No tutorial video available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Map routes to help video sections
const getHelpSection = (pathname) => {
  const sectionMap = {
    '/dashboard': 'dashboard',
    '/member-tracker': 'member-tracker',
    '/nurturing': 'companies',
    '/contacts': 'contacts',
    '/proposals': 'proposals',
    '/contracts': 'contracts',
    '/intelligence': 'intelligence',
    '/goals': 'goals',
    '/events': 'events',
    '/lead-finder': 'lead-finder',
    '/media-production': 'media-production',
    '/documentation': 'documentation',
    '/support-request': 'bugs-requests',
    '/tasks': 'tasks',
  };
  
  // Check exact match first
  if (sectionMap[pathname]) return sectionMap[pathname];
  
  // Check prefix match for nested routes
  for (const [route, section] of Object.entries(sectionMap)) {
    if (pathname.startsWith(route)) return section;
  }
  
  // Board view routes - these are member tracker boards
  if (pathname.startsWith('/board/')) {
    return 'member-tracker';
  }
  
  // Default for admin pages
  if (pathname.startsWith('/admin') || pathname.startsWith('/manage') || pathname.startsWith('/users') || pathname.startsWith('/email')) {
    return 'admin';
  }
  
  return null;
};

const AppLayout = ({ children, title, fullWidth = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(true);
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const [newRequestsCount, setNewRequestsCount] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [taskStats, setTaskStats] = useState({ overdue_tasks: 0, today_tasks: 0, urgency_score: 0, my_tasks: 0 });

  // Fetch new requests count for admin notification badge
  useEffect(() => {
    const fetchNotificationData = async () => {
      try {
        // Get current user to check role
        const userResponse = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUserRole(userData.role);
          
          // Only fetch count if admin
          if (userData.role === 'admin') {
            const countResponse = await fetch(`${BACKEND_URL}/api/support-requests/count-new`, { credentials: 'include' });
            if (countResponse.ok) {
              const countData = await countResponse.json();
              setNewRequestsCount(countData.count);
            }
          }
          
          // Fetch task stats for all users
          const taskStatsResponse = await fetch(`${BACKEND_URL}/api/tasks/stats`, { credentials: 'include' });
          if (taskStatsResponse.ok) {
            const statsData = await taskStatsResponse.json();
            setTaskStats(statsData);
          }
        }
      } catch (error) {
        console.error('Failed to fetch notification data:', error);
      }
    };

    fetchNotificationData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchNotificationData, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Get urgency color based on weighted score
  const getUrgencyColor = (score) => {
    if (score >= 50) return 'bg-red-600';
    if (score >= 30) return 'bg-red-500';
    if (score >= 15) return 'bg-orange-500';
    if (score >= 1) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // CRM Section - Main business items
  const crmMenuItems = [
    { icon: Layers, label: 'Member Tracker', path: '/member-tracker' },
    { icon: Crosshair, label: 'Lead Finder', path: '/lead-finder' },
    { icon: Heart, label: 'Companies', path: '/nurturing' },
    { icon: UserCircle, label: 'Contacts', path: '/contacts' },
    { icon: FileSignature, label: 'Proposals', path: '/proposals' },
    { icon: DollarSign, label: 'Contracts & Payments', path: '/contracts' },
  ];

  // Tasks Sub-menu
  const taskMenuItems = [
    { label: 'My Tasks', path: '/tasks', count: taskStats.my_tasks },
    { label: "Today's Tasks", path: '/tasks/today', count: taskStats.today_tasks },
    { label: 'Overdue Tasks', path: '/tasks/overdue', count: taskStats.overdue_tasks, isOverdue: true },
    { label: 'All Tasks', path: '/tasks/all' }
  ];

  // Analytics Section
  const analyticsMenuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    { icon: BarChart3, label: 'Intelligence', path: '/intelligence' },
    { icon: Target, label: 'Goals', path: '/goals' },
  ];

  // Operations Section
  const operationsMenuItems = [
    { icon: Calendar, label: 'Events Calendar', path: '/events' },
    { icon: Film, label: 'Media Production', path: '/media-production' },
    { icon: Newspaper, label: 'Press Releases', path: '/press-releases' },
  ];

  // Support Section
  const supportMenuItems = [
    { icon: BookOpen, label: 'Documentation', path: '/documentation' },
    { icon: MessageSquarePlus, label: 'Bugs & Requests', path: '/support-request' },
  ];

  const adminMenuItems = [
    { icon: Users, label: 'Users', path: '/users' },
    { icon: Package, label: 'Products', path: '/admin/products' },
    { icon: BookOpen, label: 'Section Templates', path: '/proposals/sections' },
    { icon: CreditCard, label: 'Payment Settings', path: '/admin/stripe' },
    { icon: Database, label: 'Data Import/Export', path: '/admin/data-import' },
    { icon: Video, label: 'Help Videos', path: '/admin/help-videos' },
    { icon: ListTodo, label: 'Product Backlog', path: '/admin/backlog' },
    { icon: ClipboardList, label: 'Manage Requests', path: '/manage-requests', showBadge: true },
    { icon: GitBranch, label: 'Edit Members Workflow', path: '/manage-workflow' },
    { icon: Film, label: 'Edit Media Workflow', path: '/manage-media-workflow' },
    { icon: Mail, label: 'System Emails', path: '/admin/system-emails' },
    { icon: FileText, label: 'Edit Landing Page', path: '/landing-cms' },
    { icon: Mail, label: 'Brevo Integration', path: '/admin/brevo' },
    { icon: Mail, label: 'Gmail Settings', path: '/admin/gmail' },
    { icon: Workflow, label: 'N8N Integration', path: '/admin/n8n' },
    { icon: Database, label: 'Sandbox', path: '/admin/sandbox' },
  ];

  // Check if current path is an admin path
  const isAdminPath = adminMenuItems.some(item => location.pathname === item.path);

  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-black' : 'bg-white'}`}>
      {/* Space Background for Dark Mode */}
      {isDark && <SpaceBackground />}
      
      <aside 
        className={`${
          collapsed ? 'w-16' : 'w-64'
        } ${isDark ? 'bg-[#0A0A0A]/80 backdrop-blur-sm border-white/10' : 'bg-gray-100 border-gray-200'} border-r transition-all duration-300 flex flex-col relative z-10`}
      >
        <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between`}>
          {!collapsed && (
            <h1 className="text-xl font-bold text-[#A2FF00]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              QSD
            </h1>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-600 hover:text-[#A2FF00]'} transition-colors`}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {/* CRM Section */}
          {!collapsed && (
            <div className={`px-4 py-1 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              CRM
            </div>
          )}
          {crmMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                  isActive
                    ? isDark
                      ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-l-4 border-[#A2FF00]'
                      : 'bg-[#A2FF00] text-black border-l-4 border-[#A2FF00] font-semibold'
                    : isDark 
                      ? 'text-gray-400 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-black hover:bg-gray-200'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </button>
            );
          })}

          {/* Tasks Section */}
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            {/* Tasks header - black text, colored bar only when urgent */}
            <button
              onClick={() => !collapsed && setTasksExpanded(!tasksExpanded)}
              data-testid="nav-tasks"
              className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                location.pathname.startsWith('/tasks')
                  ? isDark
                    ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-l-4 border-[#A2FF00]'
                    : 'bg-[#A2FF00] text-black border-l-4 border-[#A2FF00] font-semibold'
                  : taskStats.urgency_score >= 15
                    ? `border-l-4 ${taskStats.urgency_score >= 50 ? 'border-red-600' : taskStats.urgency_score >= 30 ? 'border-red-500' : 'border-orange-500'} ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-700 hover:text-black hover:bg-gray-200'}`
                    : isDark 
                      ? 'text-gray-400 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-black hover:bg-gray-200'
              }`}
            >
              <CheckSquare className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <>
                  <span className="text-sm font-medium flex-1 text-left">Tasks</span>
                  {taskStats.overdue_tasks > 0 && (
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                      taskStats.urgency_score >= 30 ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'
                    }`}>
                      {taskStats.overdue_tasks}
                    </span>
                  )}
                  {tasksExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </>
              )}
            </button>

            {/* Task Sub-items */}
            {(tasksExpanded || collapsed) && (
              <div className={collapsed ? '' : `ml-4 border-l ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                {taskMenuItems.map((item) => {
                  const isActive = location.pathname === item.path || 
                    (item.path === '/tasks' && location.pathname === '/tasks');
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 transition-colors ${
                        isActive
                          ? isDark
                            ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-l-4 border-[#A2FF00] -ml-px'
                            : 'bg-[#A2FF00] text-black border-l-4 border-[#A2FF00] -ml-px font-semibold'
                          : isDark 
                            ? 'text-gray-500 hover:text-white hover:bg-white/5'
                            : 'text-gray-600 hover:text-black hover:bg-gray-200'
                      }`}
                    >
                      <CheckSquare className="w-4 h-4 flex-shrink-0" />
                      {!collapsed && (
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-sm">{item.label}</span>
                          {item.count !== undefined && item.count > 0 && (
                            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                              item.isOverdue 
                                ? 'bg-red-500 text-white' 
                                : isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {item.count}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Analytics Section */}
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            {!collapsed && (
              <div className={`px-4 py-1 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Analytics
              </div>
            )}
            {analyticsMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                    isActive
                      ? isDark
                        ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-l-4 border-[#A2FF00]'
                        : 'bg-[#A2FF00] text-black border-l-4 border-[#A2FF00] font-semibold'
                      : isDark 
                        ? 'text-gray-400 hover:text-white hover:bg-white/5'
                        : 'text-gray-700 hover:text-black hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              );
            })}
          </div>

          {/* Operations Section */}
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            {!collapsed && (
              <div className={`px-4 py-1 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Operations
              </div>
            )}
            {operationsMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                    isActive
                      ? isDark
                        ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-l-4 border-[#A2FF00]'
                        : 'bg-[#A2FF00] text-black border-l-4 border-[#A2FF00] font-semibold'
                      : isDark 
                        ? 'text-gray-400 hover:text-white hover:bg-white/5'
                        : 'text-gray-700 hover:text-black hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              );
            })}
          </div>

          {/* Support Section */}
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            {!collapsed && (
              <div className={`px-4 py-1 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Support
              </div>
            )}
            {supportMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                    isActive
                      ? isDark
                        ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-l-4 border-[#A2FF00]'
                        : 'bg-[#A2FF00] text-black border-l-4 border-[#A2FF00] font-semibold'
                      : isDark 
                        ? 'text-gray-400 hover:text-white hover:bg-white/5'
                        : 'text-gray-700 hover:text-black hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              );
            })}
          </div>

          {/* Admin Section - Only visible for admin users */}
          {userRole === 'admin' && (
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <button
              onClick={() => !collapsed && setAdminExpanded(!adminExpanded)}
              data-testid="nav-admin"
              className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                isAdminPath
                  ? isDark
                    ? 'bg-[#A2FF00]/10 text-[#A2FF00]'
                    : 'bg-[#A2FF00] text-black font-semibold'
                  : isDark 
                    ? 'text-gray-400 hover:text-white hover:bg-white/5'
                    : 'text-gray-700 hover:text-black hover:bg-gray-200'
              }`}
            >
              <div className="relative">
                <Settings className="w-5 h-5 flex-shrink-0" />
                {newRequestsCount > 0 && collapsed && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </div>
              {!collapsed && (
                <>
                  <span className="text-sm font-medium flex-1 text-left">Admin</span>
                  {newRequestsCount > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full mr-1">
                      {newRequestsCount}
                    </span>
                  )}
                  {adminExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </>
              )}
            </button>

            {/* Admin Sub-items */}
            {(adminExpanded || collapsed) && (
              <div className={collapsed ? '' : `ml-4 border-l ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                {adminMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const showNotificationBadge = item.showBadge && newRequestsCount > 0;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 transition-colors ${
                        isActive
                          ? isDark
                            ? 'bg-[#A2FF00]/10 text-[#A2FF00] border-l-4 border-[#A2FF00] -ml-px'
                            : 'bg-[#A2FF00] text-black border-l-4 border-[#A2FF00] -ml-px font-semibold'
                          : isDark 
                            ? 'text-gray-500 hover:text-white hover:bg-white/5'
                            : 'text-gray-600 hover:text-black hover:bg-gray-200'
                      }`}
                    >
                      <div className="relative">
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {showNotificationBadge && collapsed && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                        )}
                      </div>
                      {!collapsed && (
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-sm">{item.label}</span>
                          {showNotificationBadge && (
                            <span className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                              {newRequestsCount}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          )}
        </nav>

        <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <button
            onClick={() => navigate('/settings')}
            data-testid="settings-button"
            className={`w-full px-4 py-3 flex items-center gap-3 ${isDark ? 'text-gray-400 hover:bg-white/5 hover:text-[#A2FF00]' : 'text-gray-600 hover:bg-black/5 hover:text-[#A2FF00]'} rounded-sm transition-colors mb-1`}
          >
            <UserCircle className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Settings</span>}
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className={`w-full px-4 py-3 flex items-center gap-3 ${isDark ? 'text-gray-400 hover:bg-white/5' : 'text-gray-600 hover:bg-black/5'} hover:text-[#FF003C] rounded-sm transition-colors`}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto relative z-10">
        <div className={`${isDark ? 'bg-[#0A0A0A]/80 backdrop-blur-sm border-white/10' : 'bg-gray-50 border-gray-200'} border-b px-8 py-6 flex items-center justify-between`}>
          <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {title}
          </h2>
          <div className="flex items-center gap-4">
            {/* Task Notifications */}
            <div className="flex items-center gap-2">
              {taskStats.overdue_tasks > 0 && (
                <button
                  onClick={() => navigate('/tasks/overdue')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm ${getUrgencyColor(taskStats.urgency_score)} hover:opacity-90 transition-opacity`}
                  title="Overdue tasks"
                >
                  <AlertCircle className={`w-4 h-4 text-white ${taskStats.urgency_score >= 30 ? 'animate-pulse' : ''}`} />
                  <span className="text-white font-bold text-sm">{taskStats.overdue_tasks} overdue</span>
                </button>
              )}
              {taskStats.today_tasks > 0 && (
                <button
                  onClick={() => navigate('/tasks/today')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm ${isDark ? 'bg-green-500/20 hover:bg-green-500/30' : 'bg-green-100 hover:bg-green-200'} transition-colors`}
                  title="Tasks due today"
                >
                  <CheckSquare className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span className={`font-bold text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>{taskStats.today_tasks} today</span>
                </button>
              )}
            </div>
            <VideoHelpIcon section={getHelpSection(location.pathname)} />
            <ThemeToggle />
          </div>
        </div>
        <div className={fullWidth ? '' : 'p-8'}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
